import { Package, ArrowUpDown, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { StockProduct } from "@/hooks/useProductStock";

interface ProductStockListProps {
  products: StockProduct[];
  isLoading: boolean;
  onAddMovement: (product: StockProduct) => void;
}

export function ProductStockList({
  products,
  isLoading,
  onAddMovement,
}: ProductStockListProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getStockStatus = (product: StockProduct) => {
    if (product.minimum_stock <= 0) {
      return { status: "normal", label: "Normal" };
    }
    if (product.current_stock <= 0) {
      return { status: "critical", label: "Sem Estoque" };
    }
    if (product.current_stock < product.minimum_stock) {
      return { status: "low", label: "Baixo" };
    }
    return { status: "normal", label: "Normal" };
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground">
          Nenhum produto com controle de estoque
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Ative "Controla Estoque" nos produtos de revenda para gerenciá-los aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Produto</TableHead>
            <TableHead className="text-right">Estoque</TableHead>
            <TableHead className="text-right">Mínimo</TableHead>
            <TableHead className="text-right">Custo</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            const stockStatus = getStockStatus(product);
            const isLowStock = stockStatus.status !== "normal";

            return (
              <TableRow
                key={product.id}
                className={isLowStock ? "bg-destructive/5" : undefined}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {isLowStock && (
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                    )}
                    <div>
                      <p className="font-medium">{product.name}</p>
                      {product.categories?.name && (
                        <p className="text-xs text-muted-foreground">
                          {product.categories.name}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {product.current_stock} un
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  {product.minimum_stock} un
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(product.cost_per_unit)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant={
                      stockStatus.status === "critical"
                        ? "destructive"
                        : stockStatus.status === "low"
                        ? "outline"
                        : "secondary"
                    }
                    className={
                      stockStatus.status === "low"
                        ? "border-orange-500 text-orange-600"
                        : undefined
                    }
                  >
                    {stockStatus.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onAddMovement(product)}
                        >
                          <ArrowUpDown className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Movimentar Estoque</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
